import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Paper, Typography, Button, Table, TableHead, TableRow, TableCell,
  TableBody, TableContainer, Checkbox, Chip, Alert, Dialog, DialogTitle, DialogContent,
  DialogActions, CircularProgress, TextField, Stack,
} from '@mui/material';
import GroupsIcon from '@mui/icons-material/Groups';
import GroupAddIcon from '@mui/icons-material/GroupAdd';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { useNavigate } from 'react-router-dom';
import {
  listMacieMembers, addMacieMembers, removeMacieMember, retryMacieMember, getMacieConfig,
  resolveMacieMemberEmails,
} from '../../../services/macieApi';
import { listSecurityHubAccounts } from '../../../services/securityhubApi';
import PortalPageHeader from '../../../components/portal/PortalPageHeader';
import {
  PortalLoading, PortalEmptyState, PortalSectionHeader,
} from '../../../components/portal/PortalStates';
import OperationProgress from './OperationProgress';
import { POLL_INTERVAL_MS } from './pollInterval';
import { formatStatusLabel } from '../../../utils/statusLabel';
import { motion } from 'motion/react';
import { pageVariants, sectionVariants } from '../../../animations/variants';
import {
  CK, FONT_BODY, sectionCardSx, tableHeadRowSx, headCellSx, rowSx, bodyCellSx,
  statusChipSx, intentFromColor, dialogProps, dialogTitleSx,
} from '../portalStyles';

const MACIE_COLOR = '#0d9488';
const MACIE_COLOR_DARK = '#0B7C72';

const macieButtonSx = {
  backgroundColor: MACIE_COLOR,
  fontFamily: FONT_BODY,
  fontWeight: 600,
  textTransform: 'none',
  '&:hover': { backgroundColor: MACIE_COLOR_DARK },
  '&.Mui-disabled': { backgroundColor: 'rgba(0, 0, 0, 0.12)', color: 'rgba(0, 0, 0, 0.26)' },
};

const enrollmentHeader = (
  <PortalPageHeader
    icon={<GroupsIcon sx={{ fontSize: 24 }} />}
    title="Enrollment"
    subtitle="Manage which member accounts are enrolled in Macie"
    color={MACIE_COLOR}
  />
);

const phaseColor = (p) => ({
  enabled: 'success',
  failed: 'error',
  pending: 'default',
  enabling: 'info',
  inviting: 'info',
  accepting: 'info',
  removing: 'warning',
  disabled: 'default',
}[p] || 'default');

const activePhases = ['pending', 'enabling', 'inviting', 'accepting', 'removing'];

// Macie's GetConfig returns the decoded MacieConfigResponse (linkingRegions is a
// []string), but tolerate a raw JSON string too so the page never crashes if the
// DTO shape shifts.
const toRegionArray = (v) => {
  if (Array.isArray(v)) return v;
  try { return JSON.parse(v || '[]'); } catch (_) { return []; }
};

// One label/value row of the configuration card.
function ConfigRow({ label, value }) {
  return (
    <Stack
      direction={{ xs: 'column', sm: 'row' }}
      spacing={{ xs: 0.25, sm: 2 }}
      sx={{ py: 0.5 }}
    >
      <Typography sx={{ minWidth: 150, fontFamily: FONT_BODY, fontSize: '0.875rem', color: CK.muted }}>
        {label}
      </Typography>
      <Typography
        sx={{
          fontFamily: FONT_BODY,
          fontSize: '0.875rem',
          fontWeight: 600,
          color: CK.navy,
          wordBreak: 'break-word',
        }}
      >
        {value}
      </Typography>
    </Stack>
  );
}

export default function MacieEnrollmentPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [config, setConfig] = useState(null);
  const [members, setMembers] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [confirmRemove, setConfirmRemove] = useState(null);
  const [emailDialog, setEmailDialog] = useState({ open: false, failed: [], manual: {} });
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const pollRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      // Config + members determine whether Macie is set up; these must succeed
      // for the page to render the enrollment status.
      const [c, m] = await Promise.all([getMacieConfig(), listMacieMembers()]);
      setConfig(c?.data || null);
      setMembers(m?.data || []);
      setError(null);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
    // Candidate-account list is best-effort: never let it blank the page.
    try {
      const a = await listSecurityHubAccounts();
      setAccounts(a?.data?.accounts || a?.data || []);
    } catch (_) {
      setAccounts([]);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  // Start a poll whenever any member enters an active phase.
  // IMPORTANT: do NOT put clearInterval in this effect's cleanup. The interval
  // callback calls setMembers(next) on every tick; that produces a new array
  // reference each time, so [members] changes and React fires the cleanup —
  // killing the interval and restarting its countdown on every tick. That
  // destroy-and-recreate cycle prevents config.progress from advancing in the
  // rendered bar. Instead the interval stops itself when no active phases remain
  // (inner check below), and a separate unmount effect handles cleanup.
  useEffect(() => {
    const active = members.some((m) => activePhases.includes(m.phase));
    if (active && !pollRef.current) {
      pollRef.current = setInterval(async () => {
        try {
          // Refresh config too so the operation progress bar (config.progress)
          // advances live, not just the per-cell member phases.
          const [m, c] = await Promise.all([listMacieMembers(), getMacieConfig()]);
          const next = m?.data || [];
          setMembers(next);
          setConfig(c?.data || null);
          const stillActive = next.some((r) => activePhases.includes(r.phase));
          if (!stillActive && pollRef.current) {
            clearInterval(pollRef.current);
            pollRef.current = null;
          }
        } catch (_) { /* transient */ }
      }, POLL_INTERVAL_MS);
    }
  }, [members]);

  // Stop the interval when the component unmounts.
  useEffect(() => {
    return () => {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };
  }, []);

  if (loading) {
    return (
      <motion.div variants={pageVariants} initial="initial" animate="animate">
        <motion.div variants={sectionVariants}>{enrollmentHeader}</motion.div>
        <motion.div variants={sectionVariants}>
          <Paper elevation={0} sx={sectionCardSx}>
            <PortalLoading label="Loading enrollment…" color={MACIE_COLOR} />
          </Paper>
        </motion.div>
      </motion.div>
    );
  }
  if (!config || config.status === 'disabled') {
    return (
      <motion.div variants={pageVariants} initial="initial" animate="animate">
        <motion.div variants={sectionVariants}>{enrollmentHeader}</motion.div>
        <motion.div variants={sectionVariants}>
          <Alert severity="info" action={
            <Button color="inherit" size="small" onClick={() => navigate('/securityhub/macie')}>
              Go to setup
            </Button>
          }>
            Macie is not set up yet for this customer. Complete the setup wizard first to enroll member accounts.
          </Alert>
        </motion.div>
      </motion.div>
    );
  }

  const worstPhase = (phases) => {
    if (phases.includes('failed')) return 'failed';
    if (phases.includes('pending')) return 'pending';
    const active = ['enabling', 'inviting', 'accepting', 'removing'];
    for (const p of active) if (phases.includes(p)) return p;
    if (phases.every((p) => p === 'enabled')) return 'enabled';
    if (phases.every((p) => p === 'disabled')) return 'disabled';
    return phases[0];
  };

  const byAccount = new Map();
  for (const m of members) {
    const prev = byAccount.get(m.accountId) || {
      accountId: m.accountId, phases: [], errors: [], failedRegions: [],
    };
    prev.phases.push(m.phase);
    if (m.phase === 'failed' && m.region) prev.failedRegions.push(m.region);
    if (m.errorMessage) prev.errors.push(`${m.region}: ${m.errorMessage}`);
    byAccount.set(m.accountId, prev);
  }
  // A fully-disabled (previously-removed) member is treated as NOT enrolled: it
  // drops out of the enrolled list and reappears under "Not enrolled" so it can
  // be re-enrolled through the normal Add flow (which resolves its root email).
  const enrolledAccounts = [...byAccount.values()].filter(
    (row) => worstPhase(row.phases) !== 'disabled',
  );
  const enrolledIds = new Set(enrolledAccounts.map((r) => r.accountId));
  const notEnrolled = accounts.filter((a) => !enrolledIds.has(a.accountId));

  const nameById = {};
  accounts.forEach((a) => { if (a.accountId) nameById[a.accountId] = a.name || a.accountName || ''; });
  const acctLabel = (id) => (nameById[id] ? `${nameById[id]} (${id})` : id);

  const submitAdd = async (accountIds, memberEmails) => {
    setBusy(true);
    try {
      await addMacieMembers(accountIds, memberEmails);
      setSelected(new Set());
      setEmailDialog({ open: false, failed: [], manual: {} });
      await load();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  const onAdd = async () => {
    const selectedIds = [...selected];
    setBusy(true);
    try {
      const res = await resolveMacieMemberEmails(selectedIds);
      const failed = (res?.data?.results || []).filter((x) => !x.resolved);
      setBusy(false);
      if (failed.length === 0) {
        await submitAdd(selectedIds, {});
      } else {
        setEmailDialog({ open: true, failed, manual: {} });
      }
    } catch (e) {
      setError(e.message);
      setBusy(false);
    }
  };

  const onConfirmEmails = async () => {
    await submitAdd([...selected], emailDialog.manual);
  };

  const emailDialogComplete = emailDialog.failed.every(
    (f) => (emailDialog.manual[f.accountId] || '').trim() !== '',
  );

  const onRemove = async (accountId) => {
    setBusy(true);
    try {
      await removeMacieMember(accountId);
      setConfirmRemove(null);
      await load();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  // Macie retry is per (account, region); replay every failed region for the row.
  // One region's failed retry request must not abort the remaining regions, and
  // the list must refresh regardless — regions already re-queued server-side
  // would otherwise keep rendering their stale "failed" phase.
  const onRetry = async (accountId, regions) => {
    setBusy(true);
    const failures = [];
    try {
      for (const region of regions) {
        try {
          // eslint-disable-next-line no-await-in-loop -- intentional: one region at a time
          await retryMacieMember({ accountId, region });
        } catch (e) {
          failures.push(`${region}: ${e.message}`);
        }
      }
      if (failures.length) setError(`Retry failed for ${failures.join('; ')}`);
    } finally {
      await load().catch((e) => setError(e.message));
      setBusy(false);
    }
  };

  const linkingRegionsDisplay = toRegionArray(config.linkingRegions).join(', ');

  return (
    <motion.div variants={pageVariants} initial="initial" animate="animate">
      <motion.div variants={sectionVariants}>{enrollmentHeader}</motion.div>
      {error && (
        <motion.div variants={sectionVariants}>
          <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
        </motion.div>
      )}

      <motion.div variants={sectionVariants}>
      <Paper elevation={0} sx={{ ...sectionCardSx, mb: 3 }}>
        <PortalSectionHeader
          icon={<InfoOutlinedIcon sx={{ fontSize: 20 }} />}
          title="Configuration"
          subtitle="Where Macie is administered from and which regions it covers"
          color={MACIE_COLOR}
        />
        <ConfigRow label="Admin account" value={config.adminAccountId} />
        <ConfigRow label="Home region" value={config.homeRegion} />
        <ConfigRow label="Linking regions" value={linkingRegionsDisplay} />
      </Paper>
      </motion.div>

      {(config?.status === 'in_progress' || members.some((m) => activePhases.includes(m.phase))) && (
        <motion.div variants={sectionVariants}>
          <OperationProgress percent={config.progress} label={config.progressLabel || 'Working…'} sx={{ mb: 3 }} />
        </motion.div>
      )}

      <motion.div variants={sectionVariants}>
      <Paper elevation={0} sx={{ ...sectionCardSx, mb: 3 }}>
        <PortalSectionHeader
          icon={<GroupsIcon sx={{ fontSize: 20 }} />}
          title="Enrolled accounts"
          subtitle="Accounts running Macie in every linking region"
          color={MACIE_COLOR}
          action={(
            <Chip
              size="small"
              label={`${enrolledAccounts.length} enrolled`}
              sx={statusChipSx('info')}
            />
          )}
        />
        <TableContainer sx={{ overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={tableHeadRowSx}>
                <TableCell sx={headCellSx}>Account</TableCell>
                <TableCell sx={headCellSx}>Status</TableCell>
                <TableCell align="right" sx={headCellSx}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {enrolledAccounts.map((row, index) => {
                const phase = worstPhase(row.phases);
                const isAdmin = row.accountId === config.adminAccountId;
                return (
                  <TableRow key={row.accountId} hover sx={rowSx(index)}>
                    <TableCell sx={bodyCellSx}>
                      {acctLabel(row.accountId)}
                      {isAdmin && <Chip label="admin" size="small" sx={{ ml: 1, ...statusChipSx('neutral') }} />}
                    </TableCell>
                    <TableCell sx={bodyCellSx}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Chip label={formatStatusLabel(phase)} size="small" sx={statusChipSx(intentFromColor(phaseColor(phase)))} />
                        {activePhases.includes(phase) && <CircularProgress size={14} thickness={5} sx={{ color: MACIE_COLOR }} />}
                      </Stack>
                      {row.errors.length > 0 && (
                        <Typography
                          sx={{
                            display: 'block',
                            mt: 0.5,
                            fontFamily: FONT_BODY,
                            fontSize: '0.75rem',
                            color: CK.red,
                            wordBreak: 'break-word',
                          }}
                        >
                          {row.errors.join(' • ')}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="right" sx={bodyCellSx}>
                      {!isAdmin && phase === 'failed' && (
                        <Button size="small" color="warning" disabled={busy} onClick={() => onRetry(row.accountId, row.failedRegions)} sx={{ mr: 1, fontFamily: FONT_BODY, fontWeight: 600, textTransform: 'none' }}>
                          Retry
                        </Button>
                      )}
                      {!isAdmin && phase !== 'disabled' && phase !== 'removing' && (
                        <Button size="small" color="error" onClick={() => setConfirmRemove(row.accountId)} sx={{ fontFamily: FONT_BODY, fontWeight: 600, textTransform: 'none' }}>
                          Remove
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
              {enrolledAccounts.length === 0 && (
                <TableRow>
                  <TableCell colSpan={3} align="center" sx={{ py: 4, borderBottom: 'none' }}>
                    <PortalEmptyState
                      icon={<GroupsIcon sx={{ fontSize: 28 }} />}
                      title="No accounts enrolled yet"
                      description="Pick accounts from the list below and add them to start collecting Macie posture."
                      color={MACIE_COLOR}
                      py={2}
                    />
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
      </motion.div>

      <motion.div variants={sectionVariants}>
      <Paper elevation={0} sx={sectionCardSx}>
        <PortalSectionHeader
          icon={<GroupAddIcon sx={{ fontSize: 20 }} />}
          title="Not enrolled"
          subtitle="Linked accounts that are not yet running Macie"
          color={MACIE_COLOR}
          action={(
            <Button
              variant="contained"
              disabled={busy || selected.size === 0}
              onClick={onAdd}
              sx={macieButtonSx}
            >
              Add {selected.size} to Macie
            </Button>
          )}
        />
        <TableContainer sx={{ overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={tableHeadRowSx}>
                <TableCell padding="checkbox">
                  <Checkbox
                    indeterminate={notEnrolled.some((a) => selected.has(a.accountId)) && !notEnrolled.every((a) => selected.has(a.accountId))}
                    checked={notEnrolled.length > 0 && notEnrolled.every((a) => selected.has(a.accountId))}
                    disabled={notEnrolled.length === 0}
                    onChange={(e) => setSelected(e.target.checked ? new Set(notEnrolled.map((a) => a.accountId)) : new Set())}
                  />
                </TableCell>
                <TableCell sx={headCellSx}>Account</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {notEnrolled.map((a, index) => (
                <TableRow key={a.accountId} hover sx={rowSx(index)}>
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={selected.has(a.accountId)}
                      onChange={(e) => {
                        const n = new Set(selected);
                        if (e.target.checked) n.add(a.accountId);
                        else n.delete(a.accountId);
                        setSelected(n);
                      }}
                    />
                  </TableCell>
                  <TableCell sx={bodyCellSx}>{a.accountName || a.name || a.accountId} ({a.accountId})</TableCell>
                </TableRow>
              ))}
              {notEnrolled.length === 0 && (
                <TableRow>
                  <TableCell colSpan={2} align="center" sx={{ py: 4, borderBottom: 'none' }}>
                    <PortalEmptyState
                      icon={<CheckCircleOutlineIcon sx={{ fontSize: 28 }} />}
                      title="Every linked account is enrolled"
                      description="Nothing left to add — new accounts appear here once they join the organization."
                      color={MACIE_COLOR}
                      py={2}
                    />
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
      </motion.div>

      <Dialog
        open={!!confirmRemove}
        onClose={() => setConfirmRemove(null)}
        {...dialogProps}
        maxWidth="xs"
      >
        <DialogTitle sx={dialogTitleSx}>Remove account from Macie</DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Typography sx={{ fontFamily: FONT_BODY, fontSize: '0.875rem', color: CK.body, mt: 1 }}>
            Remove <b>{confirmRemove}</b>? Macie in that account will be disabled.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setConfirmRemove(null)} sx={{ fontFamily: FONT_BODY, fontWeight: 600, textTransform: 'none', color: CK.body }}>Cancel</Button>
          <Button color="error" variant="contained" onClick={() => onRemove(confirmRemove)} disabled={busy} sx={{ fontFamily: FONT_BODY, fontWeight: 600, textTransform: 'none' }}>Remove</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={emailDialog.open}
        onClose={() => setEmailDialog({ open: false, failed: [], manual: {} })}
        {...dialogProps}
      >
        <DialogTitle sx={dialogTitleSx}>Enter root emails</DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Typography sx={{ fontFamily: FONT_BODY, fontSize: '0.875rem', color: CK.body, mt: 1, mb: 2 }}>
            We could not auto-fetch the AWS root email for the accounts below.
            Enter each account&apos;s root email to enroll it in Macie.
          </Typography>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {emailDialog.failed.map((f) => (
              <Stack
                key={f.accountId}
                direction={{ xs: 'column', sm: 'row' }}
                spacing={2}
                alignItems={{ xs: 'stretch', sm: 'center' }}
              >
                <Typography
                  sx={{
                    minWidth: { xs: 0, sm: 200 },
                    fontFamily: FONT_BODY,
                    fontSize: '0.875rem',
                    color: CK.navy,
                    wordBreak: 'break-word',
                  }}
                >
                  {acctLabel(f.accountId)}
                </Typography>
                <TextField
                  size="small"
                  required
                  label="Root email"
                  sx={{ flex: 1, minWidth: 0 }}
                  value={emailDialog.manual[f.accountId] || ''}
                  onChange={(e) => setEmailDialog((d) => ({
                    ...d,
                    manual: { ...d.manual, [f.accountId]: e.target.value },
                  }))}
                  helperText={f.reason || 'Auto-fetch failed — enter this account’s AWS root email'}
                  error={!(emailDialog.manual[f.accountId] || '').trim()}
                />
              </Stack>
            ))}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            onClick={() => setEmailDialog({ open: false, failed: [], manual: {} })}
            sx={{ fontFamily: FONT_BODY, fontWeight: 600, textTransform: 'none', color: CK.body }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={onConfirmEmails}
            disabled={busy || !emailDialogComplete}
            sx={macieButtonSx}
          >
            Add to Macie
          </Button>
        </DialogActions>
      </Dialog>
    </motion.div>
  );
}
